import React, { ReactNode } from 'react'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { LanguageMyReportList } from '../../../../i18n'
import { ReportListItemType } from '../../MainMenu/ReportList/Types'
import { IsStringEmpty } from '../../../NonReact/utils/GlobalFunctions'

export const PrepareRestoreResultMessage = (
    language: CLanguageHandler,
    successCount: number,
    totalSelectedCount?: number
): string => {
    if (successCount === 0) {
        return `${language.get(LanguageMyReportList.ReportlistNoreportswererestored)} ${language.get(
            LanguageMyReportList.ReportlistRestoregenericfailurereason
        )}`
    }
    if (successCount === 1 && totalSelectedCount === 1) {
        // Report was restored successfully
        return language.get(LanguageMyReportList.ReportlistReportwasrestored)
    }
    if (successCount === totalSelectedCount) {
        // XY reports were restored successfully.
        return `${language.getInterpolated(LanguageMyReportList.ReportlistReportswererestored, successCount)}`
    }

    // X out of Y
    return `${language.getInterpolated(
        LanguageMyReportList.ReportlistXoutofyselectedreportswererestored,
        successCount,
        totalSelectedCount
    )} ${language.get(LanguageMyReportList.ReportlistRestoregenericfailurereason)}`
}

export const PrepareMoveToBinDetails = (checkedReports: ReportListItemType[]): ReactNode => {
    // Access language resources directly without context as this is top level component.
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle('LanguageMyReportList')

    const reportNames: string[] = checkedReports.map((report: ReportListItemType) => report.name)

    const concatinatedString = `${reportNames.join(', ')} ${language.get(LanguageMyReportList.ReportlistBeendeleted)}`

    return (
        <>
            <p>{concatinatedString}</p>
            <p className='padding-top'>{language.get(LanguageMyReportList.ReportlistThirtydaysremaining)}</p>
        </>
    )
}

export const PrepareMoveToBinResultMessage = (
    language: CLanguageHandler,
    successCount: number,
    totalSelectedCount?: number
): string => {
    if (successCount === 0) {
        return `${language.get(LanguageMyReportList.ReportlistNoreportsweremovedtobin)} ${language.get(
            LanguageMyReportList.ReportlistMovetobingenericfailurereason
        )}`
    }
    if (successCount === 1 && totalSelectedCount === 1) {
        // Report was restored successfully.
        return language.get(LanguageMyReportList.ReportlistReportwasmovedtobin)
    }
    if (successCount === totalSelectedCount) {
        // XY reports were moved to bin successfully.
        return `${language.getInterpolated(LanguageMyReportList.ReportlistReportsweremovedtobin, successCount)}`
    }

    // X out of Y
    return `${language.getInterpolated(
        LanguageMyReportList.ReportlistXoutofyselectedreportsweremovedtobin,
        successCount,
        totalSelectedCount
    )} ${language.get(LanguageMyReportList.ReportlistMovetobingenericfailurereason)}`
}

export const PrepareDeletePermanentlyResultMessage = (
    language: CLanguageHandler,
    successCount: number,
    totalSelectedCount?: number
): string => {
    if (successCount === 0) {
        return `${language.get(LanguageMyReportList.ReportlistNoreportsweredeletedpermanently)} ${language.get(
            LanguageMyReportList.ReportlistDeletepermanentlygenericfailurereason
        )}`
    }
    if (successCount === 1 && totalSelectedCount === 1) {
        // Report was deleted permanently.
        return language.get(LanguageMyReportList.ReportlistReportwasdeletedpermanently)
    }
    if (successCount === totalSelectedCount) {
        // XY reports were deleted permanently.
        return `${language.getInterpolated(LanguageMyReportList.ReportlistReportsweredeletedpermanently, successCount)}`
    }

    return `${language.getInterpolated(
        LanguageMyReportList.ReportlistXoutofyselectedreportsweredeletedpermanently,
        successCount,
        totalSelectedCount
    )} ${language.get(LanguageMyReportList.ReportlistDeletepermanentlygenericfailurereason)}`
}

export const PrepareCopyResultMessage = (
    language: CLanguageHandler,
    successCount: number,
    totalSelectedCount?: number
): string => {
    if (successCount === 0) {
        return `${language.get(LanguageMyReportList.ReportlistNoreportswerecopied)} ${language.get(
            LanguageMyReportList.ReportlistCopygenericfailurereason
        )}`
    }
    if (successCount === 1 && totalSelectedCount === 1) {
        // Report was copied.
        return language.get(LanguageMyReportList.ReportlistReportwascopied)
    }
    if (successCount === totalSelectedCount) {
        // XY reports were copied.
        return `${language.getInterpolated(LanguageMyReportList.ReportlistReportswerecopied, successCount)}`
    }

    // X out of Y
    return `${language.getInterpolated(
        LanguageMyReportList.ReportlistXoutofyselectedreportswerecopied,
        successCount,
        totalSelectedCount
    )} ${language.get(LanguageMyReportList.ReportlistCopygenericfailurereason)}`
}

/**
 * Match a parent folder ID.
 *
 * @param currentReportListItem - Item to assess
 * @param folders - List of folder IDs.
 */
export const matchParentFolderId = (currentReportListItem: ReportListItemType, folders: string[]): boolean =>
    !IsStringEmpty(currentReportListItem.parentFolderID) && folders.includes(currentReportListItem.parentFolderID)

/**
 * Recursively get all of the sub folders.
 *
 * @param reportList - List of report items including reports and folders.
 * @param folders - Folders to get the sub folders of.
 */
export const recursiveSubFolderGet = (reportList: ReportListItemType[], folders: string[]): string[] => {
    // Current folders sub folders.
    const subFolders: string[] = reportList
        .filter(
            (currentReportListItem) =>
                matchParentFolderId(currentReportListItem, folders) && !currentReportListItem.isReport
        )
        .map((currentReportListItem) => currentReportListItem.id)

    if (subFolders.length === 0) {
        // If no sub folders return.
        return folders
    }

    // Return concated list of folders.
    return folders.concat(subFolders.concat(recursiveSubFolderGet(reportList, subFolders)))
}
